import type { CompositionScaleBarUnits } from './model';
import { groundDistanceMetresForPhysicalLength, physicalLengthMmForGroundDistance } from './scale';

export const METRES_PER_FOOT = 0.3048;
export const METRES_PER_MILE = 1609.344;

export interface ScaleBarMeasurement {
  readonly groundDistanceMetres: number;
  readonly physicalWidthMm: number;
  readonly label: string;
  readonly midpointLabel: string;
}

function largestNiceNumberAtMost(maximum: number): number | null {
  if (!Number.isFinite(maximum) || maximum <= 0) return null;
  const exponent = Math.floor(Math.log10(maximum));
  const power = 10 ** exponent;
  const leading = maximum / power;
  const coefficient = leading >= 5 ? 5 : leading >= 2 ? 2 : 1;
  const result = coefficient * power;
  return Number.isFinite(result) && result > 0 ? result : null;
}

function displayNumber(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

export function deriveScaleBarMeasurement(
  scaleDenominator: number,
  maximumPhysicalWidthMm: number,
  units: CompositionScaleBarUnits,
): ScaleBarMeasurement | null {
  const maximumMetres = groundDistanceMetresForPhysicalLength(
    scaleDenominator,
    maximumPhysicalWidthMm,
  );
  if (maximumMetres === null) return null;

  let niceGroundMetres: number;
  let label: string;
  let midpointLabel: string;
  if (units === 'metric') {
    const useKilometres = maximumMetres >= 1000;
    const unitMetres = useKilometres ? 1000 : 1;
    const nice = largestNiceNumberAtMost(maximumMetres / unitMetres);
    if (nice === null) return null;
    midpointLabel = displayNumber(nice / 2);
    niceGroundMetres = nice * unitMetres;
    label = `${displayNumber(nice)} ${useKilometres ? 'km' : 'm'}`;
  } else {
    const useMiles = maximumMetres >= METRES_PER_MILE;
    const unitMetres = useMiles ? METRES_PER_MILE : METRES_PER_FOOT;
    const nice = largestNiceNumberAtMost(maximumMetres / unitMetres);
    if (nice === null) return null;
    midpointLabel = displayNumber(nice / 2);
    niceGroundMetres = nice * unitMetres;
    label = `${displayNumber(nice)} ${useMiles ? 'mi' : 'ft'}`;
  }

  const physicalWidthMm = physicalLengthMmForGroundDistance(scaleDenominator, niceGroundMetres);
  if (physicalWidthMm === null || physicalWidthMm > maximumPhysicalWidthMm + Number.EPSILON) {
    return null;
  }
  return { groundDistanceMetres: niceGroundMetres, physicalWidthMm, label, midpointLabel };
}

/** MapLibre bearing is clockwise map rotation, so geographic north rotates inversely on the page. */
export function northArrowRotationDegrees(bearing: number): number {
  return Number.isFinite(bearing) ? -bearing : 0;
}
